import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import type CommissionForInvoiceResponse from "entities/commissionForInvoiceResponse";
import CommissionsForInvoiceViewModel from "viewModels/timeCharges/distributors/commissionsForInvoice/commissionsForInvoiceViewModel";
import { formatAmount, translateGeneral } from "utils/localizations";
import ListView from "../../../common/list";
import FilterView from "../../../common/filter";
import { Col } from "react-bootstrap";
import Button from "@edaz/edazkit/Button/index";
import { IconType } from "@edaz/edazkit/Icon";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";

const CommissionsForInvoiceFilters: ViewComponent<CommissionsForInvoiceViewModel> = observer(({ vm }) => (
  <FilterView vm={vm}>
    <div className="row">
      <FormGroup as={Col} md={6} lg={4} xl={3} target={vm.filter} property="sinceDate">
        <FormLabel>{vm.translate("filterFrom")}</FormLabel>
        <AdvancedSelect items={vm.startOfMonthOptions} placeholder={translateGeneral("select")} />
      </FormGroup>
      <FormGroup as={Col} md={6} lg={4} xl={3} target={vm.filter} property="untilDate">
        <FormLabel>{vm.translate("filterTo")}</FormLabel>
        <AdvancedSelect items={vm.endOfMonthOptions} placeholder={translateGeneral("select")} />
      </FormGroup>
      <FormGroup as={Col} md={6} lg={4} xl={3} target={vm.filter} property="distributorId">
        <FormLabel>{vm.translate("distributorId")}</FormLabel>
        <AdvancedSelect items={vm.distributorOptions} placeholder={translateGeneral("select")} />
      </FormGroup>
    </div>
  </FilterView>
));

const CommissionsForInvoiceView: ViewComponent<CommissionsForInvoiceViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<CommissionForInvoiceResponse>[] = [
    {
      property: "commissionPeriod",
      title: vm.translateAttribute("commissionPeriod"),
    },
    {
      property: "distributorName",
      title: vm.translateAttribute("distributorName"),
    },
    {
      property: "commissionAmount",
      title: vm.translateAttribute("commissionAmount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "totalPrice",
      title: vm.translateAttribute("totalPrice"),
      valueFormatter: props => formatAmount(props.value),
    },
  ];

  const actionButtons = (item: CommissionForInvoiceResponse) => (
    <Button
      variant="success"
      className="rounded"
      size="sm"
      iconLeft={IconType.Download}
      disabled={vm.downloadInProgress.get(item.id)}
      onClick={() => vm.onDownloadClick(item)}>
      {vm.translate("detail")}
    </Button>
  );

  return (
    <>
      <h3 className="mb-4">{vm.name}</h3>
      {!vm.activeChild && <CommissionsForInvoiceFilters vm={vm} />}
      <ListView vm={vm} columns={columns} hideTitle={true} actionButtons={actionButtons} />
    </>
  );
});

registerView(CommissionsForInvoiceView, CommissionsForInvoiceViewModel);
